import React, { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomTextInput from '@/components/CustomTextInput'
import CustomButton from '@/components/CustomButton'
import { Toast } from 'react-vant'

export default React.memo(function ActivateCard() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    
    // 表单数据
    const [formData, setFormData] = useState({
        cardholderName: '',
        last4Digits: '',
        expiryDate: '',
        cvv: ''
    })

    // 错误状态
    const [errors, setErrors] = useState({
        cardholderName: '',
        last4Digits: '',
        expiryDate: '',
        cvv: ''
    })

    // 加载状态
    const [loading, setLoading] = useState(false)

    // 更新表单字段
    const updateField = useCallback((field: keyof typeof formData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
        // 清除该字段的错误
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }))
        }
    }, [errors])

    // 验证表单
    const validateForm = useCallback(() => {
        const newErrors = {
            cardholderName: '',
            last4Digits: '',
            expiryDate: '',
            cvv: ''
        }

        // 验证持卡人姓名
        if (!formData.cardholderName.trim()) {
            newErrors.cardholderName = t('请输入持卡人姓名')
        }

        // 验证卡号后4位
        if (!formData.last4Digits.trim()) {
            newErrors.last4Digits = t('请输入卡号后4位')
        } else if (!/^\d{4}$/.test(formData.last4Digits)) {
            newErrors.last4Digits = t('请输入4位数字')
        }

        // 验证有效期
        if (!formData.expiryDate.trim()) {
            newErrors.expiryDate = t('请输入有效期')
        } else if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(formData.expiryDate)) {
            newErrors.expiryDate = t('格式错误，例如：07/29')
        }

        // 验证CVV
        if (!formData.cvv.trim()) {
            newErrors.cvv = t('请输入CVV')
        } else if (!/^\d{3}$/.test(formData.cvv)) {
            newErrors.cvv = t('请输入3位数字')
        }

        setErrors(newErrors)
        return !Object.values(newErrors).some(error => error !== '')
    }, [formData, t])

    // 处理有效期输入
    const handleExpiryDateChange = useCallback((value: string) => {
        // 只允许数字和斜杠
        let formatted = value.replace(/[^\d]/g, '')
        
        // 自动添加斜杠
        if (formatted.length >= 2) {
            formatted = formatted.slice(0, 2) + '/' + formatted.slice(2, 4)
        }
        
        updateField('expiryDate', formatted)
    }, [updateField])

    // 提交激活申请
    const handleSubmit = useCallback(async () => {
        if (!validateForm()) {
            return
        }

        setLoading(true)
        try {
            // TODO: 调用激活卡片接口
            // await APIWeb.activatePhysicalCard(formData)
            
            // 模拟API调用
            await new Promise(resolve => setTimeout(resolve, 1500))

            Toast.success({
                message: t('激活申请已提交'),
                position: 'middle',
                onClose: () => {
                    navigate('/card')
                }
            })
        } catch (error) {
            console.error('激活卡片失败:', error)
            Toast.fail({
                message: t('激活失败，请重试'),
                position: 'middle'
            })
        } finally {
            setLoading(false)
        }
    }, [formData, validateForm, t, navigate])

    return (
        <PageLayout>
            <Header
                leftChildren={<Back />}
                centerChildren={
                    <span className="text-3.75 font-medium text-black">{t('激活卡片')}</span>
                }
            />

            <div className="px-6 pt-6 pb-24">
                {/* 持卡人姓名 */}
                <div className="mb-6">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('请输入您的姓名')}</div>
                    <CustomTextInput
                        value={formData.cardholderName}
                        onChangeText={(text) => updateField('cardholderName', text)}
                        placeholder={t('例X', { example: 'LEE QUANQUAN' })}
                        isErrored={!!errors.cardholderName}
                    />
                    {errors.cardholderName && (
                        <div className="text-xs text-red-500 mt-1">{errors.cardholderName}</div>
                    )}
                </div>

                {/* 卡号后4位 */}
                <div className="mb-6">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('请输入您的卡号')}</div>
                    <CustomTextInput
                        value={formData.last4Digits}
                        onChangeText={(text) => updateField('last4Digits', text.replace(/\D/g, '').slice(0, 4))}
                        placeholder={t('例X', { example: '5238***28232' })}
                        isErrored={!!errors.last4Digits}
                        inputMode="numeric"
                        maxLength={4}
                    />
                    {errors.last4Digits && (
                        <div className="text-xs text-red-500 mt-1">{errors.last4Digits}</div>
                    )}
                </div>

                {/* 有效期和CVV */}
                <div className="flex gap-4 mb-6">
                    {/* 有效期 */}
                    <div className="flex-1">
                        <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('请输入您的有效期')}</div>
                        <CustomTextInput
                            value={formData.expiryDate}
                            onChangeText={handleExpiryDateChange}
                            placeholder={`MM/YY (${t('例X', { example: '07/29' })})`}
                            isErrored={!!errors.expiryDate}
                            inputMode="numeric"
                            maxLength={5}
                        />
                        {errors.expiryDate && (
                            <div className="text-xs text-red-500 mt-1">{errors.expiryDate}</div>
                        )}
                    </div>

                    {/* CVV */}
                    <div className="flex-1">
                        <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('安全码')}</div>
                        <CustomTextInput
                            value={formData.cvv}
                            onChangeText={(text) => updateField('cvv', text.replace(/\D/g, '').slice(0, 3))}
                            placeholder="CVV"
                            isErrored={!!errors.cvv}
                            inputMode="numeric"
                            maxLength={3}
                        />
                        {errors.cvv && (
                            <div className="text-xs text-red-500 mt-1">{errors.cvv}</div>
                        )}
                    </div>
                </div>

                {/* 提交按钮 */}
                <div className="">
                    <CustomButton
                        onClick={handleSubmit}
                        loading={loading}
                        disabled={loading}
                    >
                        {t('提交激活申请')}
                    </CustomButton>
                </div>
            </div>
        </PageLayout>
    )
})
